import { observer } from 'mobx-react-lite'
import { FC, useEffect, useState } from 'react'
import { useTreeState } from '../../../stores/Root'
import { AlternativePlaybackControls } from '../common/PlaybackControls'
import { AudioControlsProps } from './SampleTimelineControls'

export const usePlaybackControls = (audio: HTMLAudioElement) => {
  const { globalPlayer } = useTreeState()
  const [isPlaying, setIsPlaying] = useState<boolean>(false)

  const handlePlayPause = () => {
    if (audio.paused) void audio.play()
    else audio.pause()
  }

  const handleRepeat = () => {
    audio.loop = !audio.loop
    globalPlayer.toggleLoop()
  }

  const handleStop = () => {
    audio.currentTime = 0
    audio.pause()
  }

  useEffect(() => {
    const setPlayingState = (state: boolean) => () => setIsPlaying(state)
    const setPlay = setPlayingState(true)
    const setPause = setPlayingState(false)

    if (audio.paused) setPause()
    else setPlay()

    audio.addEventListener('play', setPlay)
    audio.addEventListener('pause', setPause)
    return () => {
      if (!audio) return
      audio.removeEventListener('play', setPlay)
      audio.removeEventListener('pause', setPause)
    }
  }, [audio])

  return {
    isPlaying,
    isRepeat: globalPlayer.loop,
    onPlayPause: handlePlayPause,
    onRepeat: handleRepeat,
    onStop: handleStop,
    isAutoplay: globalPlayer.autoplay,
    toggleAutoplay: globalPlayer.toggleAutoPlay,
    playNext: globalPlayer.playNextArpMedia,
    playPrevious: globalPlayer.playPreviousArpMedia,
    previousDisabled: globalPlayer.previousStack.length === 0
  }
}

const SamplePlaybackControls: FC<AudioControlsProps> = ({ audio }) => {
  const {
    isAutoplay,
    toggleAutoplay,
    playNext,
    playPrevious,
    previousDisabled,
    ...playbackControls
  } = usePlaybackControls(audio)
  return <AlternativePlaybackControls {...playbackControls} />
}

export default observer(SamplePlaybackControls)
